import { Check } from '@mui/icons-material';
import { Fade, Stack, SvgIcon } from '@mui/material';
import { useScimMethodGuide } from 'api/guide';
import { useCurrentScimIdP } from 'api/idps';
import IdPChip from 'components/shared/IdPChip';
import SidebarMenuItem from 'components/SidebarMenu/SidebarMenuItem';
import SidebarMenuSection from 'components/SidebarMenu/SidebarMenuSection';
import { ROUTES } from 'constants/routes';
import { GuideType } from 'consts';
import { useSCIMGuideProgress } from 'contexts/GuideProgressContext';
import { useIdPConfigContext } from 'contexts/IdPConfigContext';
import React from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useNav, useStepNavigate } from 'utils/navigation';

interface ISCIMMenuSectionProps {
	open: boolean;
	toggleOpen: () => void;
}

const SCIMMenuSection: React.FC<ISCIMMenuSectionProps> = ({
	open,
	toggleOpen
}) => {
	const nav = useNav();
	const location = useLocation();

	const { scimGuideStepNum, scimIdpID, scimMethod, guideType } =
		useIdPConfigContext();
	const currentGuideStepNum = scimGuideStepNum;

	const { data: currentScimIdPData } = useCurrentScimIdP();
	const { data: currentScimIdPGuideData } = useScimMethodGuide(
		scimIdpID,
		scimMethod
	);

	const { isStepCompleted, completedSteps, totalSteps } =
		useSCIMGuideProgress();

	const { navigateToScimStep } = useStepNavigate();

	return (
		<SidebarMenuSection
			title={intl.tr('general.SCIMConfiguration')}
			open={open}
			onTitleItemClick={toggleOpen}
			progress={completedSteps}
			progressTotal={totalSteps}
			startIcon={<SvgIcon fontSize="small" />}
			testIdTopic="scim"
		>
			<SidebarMenuItem
				data-testid="scim-select-idp-menu-item"
				active={Boolean(matchPath(ROUTES.selectScim, location.pathname))}
				buttonBaseProps={{ onClick: () => nav(ROUTES.selectScim) }}
				endIcon={currentScimIdPData && <IdPChip idp={currentScimIdPData} />}
				startIcon={
					<Check
						sx={{
							width: 20,
							visibility: currentScimIdPData ? 'visible' : 'hidden'
						}}
						fontSize="small"
						color="primary"
					/>
				}
			>
				{intl.tr('ssoConfig.guide.selectIdP.pageTitle')}
			</SidebarMenuItem>
			{currentScimIdPGuideData?.guide.map((step, i) => (
				<Fade key={step.title} in style={{ transitionDelay: `${i * 75}ms` }}>
					<Stack>
						<SidebarMenuItem
							data-testid="guide-step-menu-item"
							active={
								guideType === GuideType.SCIM &&
								Boolean(matchPath(ROUTES.guideStep, location.pathname)) &&
								currentGuideStepNum === i + 1
							}
							buttonBaseProps={{ onClick: () => navigateToScimStep(i + 1) }}
							startIcon={
								<Check
									sx={{
										width: 20,
										visibility: isStepCompleted(i + 1) ? 'visible' : 'hidden'
									}}
									fontSize="small"
									color="primary"
								/>
							}
						>
							{step.title}
						</SidebarMenuItem>
					</Stack>
				</Fade>
			))}
		</SidebarMenuSection>
	);
};

export default SCIMMenuSection;
